import { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, Typography, alpha } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RouteIcon from '@mui/icons-material/Route';
import { DeliveryOverview } from './list';
import { DeliveryScheduleTab } from './schedule';
import { RoutesTab } from './routes-tab';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deliveries-tabpanel-${index}`}
      aria-labelledby={`deliveries-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const DeliveriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  // Get tab from URL, default to 'overview'
  const tabParam = searchParams.get('tab');
  const getTabIndex = (tab: string | null): number => {
    switch (tab) {
      case 'schedule':
        return 1;
      case 'routes':
        return 2;
      default:
        return 0;
    }
  };

  const [tabValue, setTabValue] = useState(getTabIndex(tabParam));

  // Update tab when URL changes
  useEffect(() => {
    setTabValue(getTabIndex(tabParam));
  }, [tabParam]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tabNames = ['overview', 'schedule', 'routes'];
    setSearchParams({ tab: tabNames[newValue] });
    setTabValue(newValue);
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 3, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalShippingIcon sx={{ color: palette.primary, fontSize: 24 }} />
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            LOGISTICS MANAGEMENT
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.extrabold,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          DELIVERIES
        </Typography>
      </Box>

      {/* Tabs Header */}
      <Box
        sx={{
          mb: 3,
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.1),
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Deliveries tabs"
          sx={{
            minHeight: 56,
            '& .MuiTabs-indicator': {
              backgroundColor: palette.primary,
              height: 3,
            },
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'uppercase',
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.widest,
              color: colors.textMuted,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.05),
                color: colors.textPrimary,
              },
              '&.Mui-selected': {
                color: palette.primary,
                backgroundColor: alpha(palette.primary, 0.1),
              },
            },
          }}
        >
          <Tab
            icon={<LocalShippingIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Overview"
            id="deliveries-tab-0"
            aria-controls="deliveries-tabpanel-0"
          />
          <Tab
            icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="To Schedule"
            id="deliveries-tab-1"
            aria-controls="deliveries-tabpanel-1"
          />
          <Tab
            icon={<RouteIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Saved Routes"
            id="deliveries-tab-2"
            aria-controls="deliveries-tabpanel-2"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <DeliveryOverview />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <DeliveryScheduleTab />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <RoutesTab />
      </TabPanel>
    </Box>
  );
};
